import { Link } from '@inertiajs/react';
import { ChevronDown, Facebook, Globe, Menu, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const baseUrl = import.meta.env.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

export default function LandingNavbar() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [scrolled, setScrolled] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);

    // Get current path for active link detection
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

    // Helper function to check if link is active
    const isActive = (href: string) => {
        if (href === '/') {
            return currentPath === '/';
        }
        return currentPath.startsWith(href);
    };

    // Helper function to check if dropdown should be active
    const isDropdownActive = (paths: string[]) => {
        return paths.some((path) => currentPath.startsWith(path));
    };

    const aboutPaths = ['/about', '/governance', '/downloads'];
    const academicPaths = ['/institutes', '/academic-policies'];
    const admissionPaths = ['/course-offerings', '/scholarships-and-grants'];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setActiveDropdown(null);
            }
        };

        const handleScroll = () => {
            if (window.scrollY > 20) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const toggleDropdown = (menu: string) => {
        setSearchOpen(false);
        setActiveDropdown(activeDropdown === menu ? null : menu);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            window.location.href = `/search?query=${encodeURIComponent(searchQuery)}`;
        }
    };

    // Helper to generate link class names
    const getLinkClass = (href: string, isDesktop = true) => {
        const baseClass = isDesktop
            ? 'px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 border border-transparent shrink-0'
            : 'block px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200';
        const activeClass = isDesktop ? 'border-[#00963f]/25 bg-[#00963f]/15 text-[#21d36b] shadow-sm' : 'bg-[#00963f]/15 text-[#21d36b]';
        const inactiveClass = 'text-gray-300 hover:text-white hover:bg-[oklch(0.2_0_0)]/50';

        return `${baseClass} ${isActive(href) ? activeClass : inactiveClass}`;
    };

    const getDropdownLinkClass = (href: string) => {
        return `block px-4 py-2.5 rounded-lg text-sm transition-all duration-200 ${
            isActive(href) ? 'bg-[#00963f]/15 font-semibold text-[#21d36b]' : 'text-gray-200 hover:bg-[oklch(0.24_0_0)] hover:text-white'
        }`;
    };

    const getDropdownButtonClass = (paths: string[]) => {
        return `px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-all duration-200 border border-transparent shrink-0 ${
            isDropdownActive(paths)
                ? 'border-[#00963f]/25 bg-[#00963f]/15 text-[#21d36b]'
                : 'text-gray-300 hover:text-white hover:bg-[oklch(0.2_0_0)]/50'
        }`;
    };

    return (
        <>
            {/* Top strip */}
            <div className="hidden border-b border-[#008538] bg-[#00963f] text-white lg:block">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex h-10 items-center justify-between text-sm">
                        <div className="flex items-center gap-1">
                            <span className="text-xs font-semibold tracking-wide text-white">BE NURTURED, BE COMPETITIVE, CHOOSE NC!</span>
                        </div>

                        <div className="flex items-center gap-6">
                            <a
                                href="https://www.facebook.com/NCnianAko"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-xs transition-colors hover:text-white/80"
                            >
                                <Facebook className="h-3.5 w-3.5" />
                                <span>facebook.com/NCnianAko</span>
                            </a>
                            <a
                                href="https://icloudph.com/nc/sias/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-xs transition-colors hover:text-white/80"
                            >
                                <Globe className="h-3.5 w-3.5" />
                                <span>SIAS Online</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <nav className="sticky top-0 z-50">
                <div
                    className={`transition-all duration-300 ${
                        scrolled
                            ? 'border-b border-[#00963f]/20 bg-[oklch(0.145_0_0)]/90 py-3 shadow-xl backdrop-blur-md'
                            : 'border-b border-[oklch(0.12_0_0)] bg-[oklch(0.145_0_0)] py-4'
                    }`}
                >
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between">
                            <Link href="/" prefetch className="group flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center overflow-hidden transition-transform duration-300 group-hover:scale-105">
                                    <img
                                        src={`${baseUrl}/images/logo.webp`}
                                        alt="NC Logo"
                                        className="h-9 w-9 object-contain"
                                        width={45}
                                        height={45}
                                        loading="eager"
                                        decoding="async"
                                    />
                                </div>
                                <div className="block">
                                    <div className="text-base leading-tight font-bold tracking-tight text-white sm:text-lg">NORTHEASTERN COLLEGE</div>
                                    <div className="text-[10px] font-semibold tracking-wider text-[#21d36b] uppercase">Santiago City, Isabela</div>
                                </div>
                            </Link>

                            {/* Desktop nav links */}
                            <div className="hidden items-center gap-1.5 lg:flex">
                                <Link href="/" prefetch className={`${getLinkClass('/')} font-semibold`}>
                                    Home
                                </Link>
                                <div className="relative" ref={activeDropdown === 'about' ? dropdownRef : null}>
                                    <button onClick={() => toggleDropdown('about')} className={getDropdownButtonClass(aboutPaths)}>
                                        About
                                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'about' ? 'rotate-180' : ''}`} />
                                    </button>

                                    {activeDropdown === 'about' && (
                                        <div className="absolute top-full left-1/2 mt-2 w-[31rem] -translate-x-1/2 rounded-xl border border-[oklch(0.22_0_0)] bg-[oklch(0.18_0_0)]/95 p-3 shadow-2xl backdrop-blur-lg">
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="space-y-1 pr-2">
                                                    <Link href="/about/school-seal" prefetch className={getDropdownLinkClass('/about/school-seal')} onClick={() => setActiveDropdown(null)}>
                                                        School Seal &amp; Motto
                                                    </Link>
                                                    <Link href="/about/history" prefetch className={getDropdownLinkClass('/about/history')} onClick={() => setActiveDropdown(null)}>
                                                        History
                                                    </Link>
                                                    <Link
                                                        href="/about/vision-and-mission"
                                                        prefetch
                                                        className={getDropdownLinkClass('/about/vision-and-mission')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        Vision &amp; Mission
                                                    </Link>
                                                    <Link
                                                        href="/about/educational-philosophy"
                                                        prefetch
                                                        className={getDropdownLinkClass('/about/educational-philosophy')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        Educational Philosophy
                                                    </Link>
                                                </div>

                                                <div className="space-y-1 border-l border-[oklch(0.22_0_0)] pl-3">
                                                    {/* <Link
                                                        href="/governance"
                                                        prefetch
                                                        className={getDropdownLinkClass('/governance')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        Governance
                                                    </Link> */}
                                                    <Link
                                                        href="/downloadable-files"
                                                        prefetch
                                                        className={getDropdownLinkClass('/downloadable-files')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        Downloadable
                                                    </Link>
                                                    <Link
                                                        href="/contact"
                                                        prefetch
                                                        className={getDropdownLinkClass('/contact')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        Contact Us
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Academic Dropdown */}
                                <div className="relative" ref={activeDropdown === 'academic' ? dropdownRef : null}>
                                    <button onClick={() => toggleDropdown('academic')} className={getDropdownButtonClass(academicPaths)}>
                                        Academic
                                        <ChevronDown
                                            className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'academic' ? 'rotate-180' : ''}`}
                                        />
                                    </button>

                                    {activeDropdown === 'academic' && (
                                        <div className="custom-scroll absolute top-full left-1/2 mt-2 max-h-[70vh] w-[54rem] -translate-x-1/2 overflow-y-auto rounded-xl border border-[oklch(0.22_0_0)] bg-[oklch(0.18_0_0)]/95 p-3 shadow-2xl backdrop-blur-lg">
                                            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_220px] gap-3">
                                                <div className="space-y-1 pr-2">
                                                    <Link
                                                        href="/institutes/basic-education-center"
                                                        prefetch
                                                        className={getDropdownLinkClass('/institutes/basic-education-center')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        Basic Education Center
                                                    </Link>
                                                    <Link
                                                        href="/institutes/college-of-accountancy-and-business-administration"
                                                        prefetch
                                                        className={getDropdownLinkClass('/institutes/college-of-accountancy-and-business-administration')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        College of Accountancy & Business
                                                    </Link>
                                                    <Link
                                                        href="/institutes/college-of-criminology"
                                                        prefetch
                                                        className={getDropdownLinkClass('/institutes/college-of-criminology')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        College of Criminology
                                                    </Link>
                                                    <Link
                                                        href="/institutes/college-of-education"
                                                        prefetch
                                                        className={getDropdownLinkClass('/institutes/college-of-education')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        College of Education
                                                    </Link>
                                                    <Link
                                                        href="/institutes/college-of-geodetic-engineering"
                                                        prefetch
                                                        className={getDropdownLinkClass('/institutes/college-of-geodetic-engineering')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        College of Geodetic Engineering
                                                    </Link>
                                                    <Link
                                                        href="/institutes/college-of-hospitality-management"
                                                        prefetch
                                                        className={getDropdownLinkClass('/institutes/college-of-hospitality-management')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        College of Hospitality Management
                                                    </Link>
                                                </div>

                                                <div className="space-y-1 border-l border-[oklch(0.22_0_0)] px-3">
                                                    <Link
                                                        href="/institutes/college-of-information-technology"
                                                        prefetch
                                                        className={getDropdownLinkClass('/institutes/college-of-information-technology')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        College of Information Technology
                                                    </Link>
                                                    <Link
                                                        href="/institutes/college-of-law"
                                                        prefetch
                                                        className={getDropdownLinkClass('/institutes/college-of-law')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        College of Law
                                                    </Link>
                                                    <Link
                                                        href="/institutes/college-of-liberal-arts"
                                                        prefetch
                                                        className={getDropdownLinkClass('/institutes/college-of-liberal-arts')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        College of Liberal Arts
                                                    </Link>
                                                    <Link
                                                        href="/institutes/school-of-midwifery"
                                                        prefetch
                                                        className={getDropdownLinkClass('/institutes/school-of-midwifery')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        School of Midwifery
                                                    </Link>
                                                    <Link
                                                        href="/institutes/college-of-nursing"
                                                        prefetch
                                                        className={getDropdownLinkClass('/institutes/college-of-nursing')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        College of Nursing (BSN)
                                                    </Link>
                                                    <Link
                                                        href="/institutes/graduate-school"
                                                        prefetch
                                                        className={getDropdownLinkClass('/institutes/graduate-school')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        Graduate School (GS)
                                                    </Link>
                                                </div>

                                                <div className="border-l border-[oklch(0.22_0_0)] pl-3">
                                                    <Link
                                                        href="/academic-policies"
                                                        prefetch
                                                        className={getDropdownLinkClass('/academic-policies')}
                                                        onClick={() => setActiveDropdown(null)}
                                                    >
                                                        Academic Policies
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <Link href="/research" prefetch className={getLinkClass('/research')}>
                                    Research
                                </Link>

                                <Link href="/sdg" prefetch className={getLinkClass('/sdg')}>
                                    Sustainability
                                </Link>

                                {/* Admission Dropdown */}
                                <div className="relative" ref={activeDropdown === 'admission' ? dropdownRef : null}>
                                    <button onClick={() => toggleDropdown('admission')} className={getDropdownButtonClass(admissionPaths)}>
                                        Admission
                                        <ChevronDown
                                            className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'admission' ? 'rotate-180' : ''}`}
                                        />
                                    </button>

                                    {activeDropdown === 'admission' && (
                                        <div className="absolute top-full left-1/2 mt-2 w-56 -translate-x-1/2 rounded-xl border border-[oklch(0.22_0_0)] bg-[oklch(0.18_0_0)]/95 p-2 shadow-2xl backdrop-blur-lg">
                                            <Link
                                                href="/course-offerings"
                                                prefetch
                                                className={getDropdownLinkClass('/course-offerings')}
                                                onClick={() => setActiveDropdown(null)}
                                            >
                                                Course Offerings
                                            </Link>
                                            <Link
                                                href="/scholarships-and-grants"
                                                prefetch
                                                className={getDropdownLinkClass('/scholarships-and-grants')}
                                                onClick={() => setActiveDropdown(null)}
                                            >
                                                Scholarships and Grants
                                            </Link>
                                        </div>
                                    )}
                                </div>

                                <Link href="/careers" prefetch className={getLinkClass('/careers')}>
                                    Careers
                                </Link>
                            </div>

                            <div className="hidden items-center gap-3 lg:flex">
                                <button
                                    onClick={() => {
                                        setActiveDropdown(null);
                                        setSearchOpen((prev) => !prev);
                                    }}
                                    className="rounded-full p-2.5 text-gray-300 transition-colors hover:bg-[oklch(0.2_0_0)]/50 hover:text-white"
                                    aria-label={searchOpen ? 'Close search' : 'Open search'}
                                    title="Search"
                                >
                                    <Search className="h-5 w-5" />
                                </button>
                            </div>

                            <button
                                onClick={() => {
                                    setMobileMenuOpen(!mobileMenuOpen);
                                    setSearchOpen(false);
                                    setActiveDropdown(null);
                                }}
                                className="rounded-full p-2.5 text-gray-300 transition-colors hover:bg-[oklch(0.2_0_0)]/50 hover:text-white lg:hidden"
                                aria-label={mobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
                            >
                                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                            </button>
                        </div>

                        {searchOpen && (
                            <div className="mt-4 hidden pb-2 lg:block">
                                <form onSubmit={handleSearch} className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search Northeastern College..."
                                        className="w-full rounded-full border border-[oklch(0.28_0_0)] bg-[oklch(0.2_0_0)] px-5 py-3 pr-12 text-sm text-white shadow-inner transition-all placeholder:text-gray-400 focus:border-transparent focus:ring-2 focus:ring-[#00963f]/45 focus:outline-none"
                                        autoFocus
                                    />
                                    <button
                                        type="submit"
                                        className="absolute top-1/2 right-2 -translate-y-1/2 p-2 text-gray-200 hover:text-white"
                                        aria-label="Submit search"
                                    >
                                        <Search className="h-5 w-5 text-[#21d36b]" />
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="border-t border-[oklch(0.12_0_0)] bg-[oklch(0.145_0_0)] shadow-inner lg:hidden">
                        <div className="custom-scroll mx-auto max-h-[calc(100vh-80px)] max-w-7xl space-y-2 overflow-y-auto px-4 py-4">
                            <form onSubmit={handleSearch} className="mb-4">
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search..."
                                        className="w-full rounded-lg border border-[oklch(0.28_0_0)] bg-[oklch(0.2_0_0)] px-4 py-2 pr-10 text-sm text-white placeholder:text-gray-400 focus:ring-2 focus:ring-[#00963f]/45 focus:outline-none"
                                    />
                                    <button
                                        type="submit"
                                        className="absolute top-1/2 right-2 -translate-y-1/2 text-gray-200 hover:text-white"
                                        aria-label="Submit search"
                                    >
                                        <Search className="h-5 w-5 text-[#21d36b]" />
                                    </button>
                                </div>
                            </form>

                            <Link href="/" prefetch className={`${getLinkClass('/', false)} font-medium`} onClick={() => setMobileMenuOpen(false)}>
                                Home
                            </Link>
                            <div className="space-y-1">
                                <button
                                    onClick={() => toggleDropdown('mobile-about')}
                                    className={`w-full justify-between text-left ${getDropdownButtonClass(aboutPaths)}`}
                                >
                                    About
                                    <ChevronDown
                                        className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'mobile-about' ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {activeDropdown === 'mobile-about' && (
                                    <div className="mt-1 ml-4 space-y-1 border-l border-[#00963f]/25 pl-4">
                                        <Link href="/about/school-seal" prefetch className={`${getDropdownLinkClass('/about/school-seal')} text-sm`} onClick={() => setMobileMenuOpen(false)}>
                                            School Seal &amp; Motto
                                        </Link>
                                        <Link href="/about/history" prefetch className={`${getDropdownLinkClass('/about/history')} text-sm`} onClick={() => setMobileMenuOpen(false)}>
                                            History
                                        </Link>
                                        <Link
                                            href="/about/vision-and-mission"
                                            prefetch
                                            className={`${getDropdownLinkClass('/about/vision-and-mission')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Vision &amp; Mission
                                        </Link>
                                        <Link
                                            href="/about/educational-philosophy"
                                            prefetch
                                            className={`${getDropdownLinkClass('/about/educational-philosophy')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Educational Philosophy
                                        </Link>
                                        {/* <Link
                                            href="/governance"
                                            prefetch
                                            className={`${getDropdownLinkClass('/governance')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Governance
                                        </Link> */}
                                        <Link
                                            href="/downloadable-files"
                                            prefetch
                                            className={`${getDropdownLinkClass('/downloadable-files')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Downloadable
                                        </Link>
                                        <Link
                                            href="/contact"
                                            prefetch
                                            className={`${getDropdownLinkClass('/contact')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Contact Us
                                        </Link>
                                    </div>
                                )}
                            </div>

                            {/* Mobile Academic */}
                            <div className="space-y-1">
                                <button
                                    onClick={() => toggleDropdown('mobile-academic')}
                                    className={`w-full text-left ${getDropdownButtonClass(academicPaths)} justify-between`}
                                >
                                    Academic
                                    <ChevronDown
                                        className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'mobile-academic' ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {activeDropdown === 'mobile-academic' && (
                                        <div className="mt-1 ml-4 space-y-1 border-l border-[#00963f]/25 pl-4">
                                        <Link
                                            href="/institutes/basic-education-center"
                                            prefetch
                                            className={`${getDropdownLinkClass('/institutes/basic-education-center')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Basic Education Center
                                        </Link>
                                        <Link
                                            href="/institutes/college-of-accountancy-and-business-administration"
                                            prefetch
                                            className={`${getDropdownLinkClass('/institutes/college-of-accountancy-and-business-administration')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            College of Accountancy & Business
                                        </Link>
                                        <Link
                                            href="/institutes/college-of-criminology"
                                            prefetch
                                            className={`${getDropdownLinkClass('/institutes/college-of-criminology')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            College of Criminology
                                        </Link>
                                        <Link
                                            href="/institutes/college-of-education"
                                            prefetch
                                            className={`${getDropdownLinkClass('/institutes/college-of-education')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            College of Education
                                        </Link>
                                        <Link
                                            href="/institutes/college-of-geodetic-engineering"
                                            prefetch
                                            className={`${getDropdownLinkClass('/institutes/college-of-geodetic-engineering')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            College of Geodetic Engineering
                                        </Link>
                                        <Link
                                            href="/institutes/college-of-hospitality-management"
                                            prefetch
                                            className={`${getDropdownLinkClass('/institutes/college-of-hospitality-management')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            College of Hospitality Management
                                        </Link>
                                        <Link
                                            href="/institutes/college-of-information-technology"
                                            prefetch
                                            className={`${getDropdownLinkClass('/institutes/college-of-information-technology')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            College of Information Technology
                                        </Link>
                                        <Link
                                            href="/institutes/college-of-law"
                                            prefetch
                                            className={`${getDropdownLinkClass('/institutes/college-of-law')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            College of Law
                                        </Link>
                                        <Link
                                            href="/institutes/college-of-liberal-arts"
                                            prefetch
                                            className={`${getDropdownLinkClass('/institutes/college-of-liberal-arts')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            College of Liberal Arts
                                        </Link>
                                        <Link
                                            href="/institutes/school-of-midwifery"
                                            prefetch
                                            className={`${getDropdownLinkClass('/institutes/school-of-midwifery')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            School of Midwifery
                                        </Link>
                                        <Link
                                            href="/institutes/college-of-nursing"
                                            prefetch
                                            className={`${getDropdownLinkClass('/institutes/college-of-nursing')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            College of Nursing (BSN)
                                        </Link>
                                        <Link
                                            href="/institutes/graduate-school"
                                            prefetch
                                            className={`${getDropdownLinkClass('/institutes/graduate-school')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Graduate School (GS)
                                        </Link>
                                        <Link
                                            href="/academic-policies"
                                            prefetch
                                            className={`${getDropdownLinkClass('/academic-policies')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Academic Policies
                                        </Link>
                                    </div>
                                )}
                            </div>

                            <Link href="/research" prefetch className={getLinkClass('/research', false)} onClick={() => setMobileMenuOpen(false)}>
                                Research
                            </Link>

                            <Link href="/sdg" prefetch className={getLinkClass('/sdg', false)} onClick={() => setMobileMenuOpen(false)}>
                                    Sustainability
                                </Link>

                            {/* Mobile Admission */}
                            <div className="space-y-1">
                                <button
                                    onClick={() => toggleDropdown('mobile-admission')}
                                    className={`w-full text-left ${getDropdownButtonClass(admissionPaths)} justify-between`}
                                >
                                    Admission
                                    <ChevronDown
                                        className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'mobile-admission' ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {activeDropdown === 'mobile-admission' && (
                                        <div className="mt-1 ml-4 space-y-1 border-l border-[#00963f]/25 pl-4">
                                        <Link
                                            href="/course-offerings"
                                            prefetch
                                            className={`${getDropdownLinkClass('/course-offerings')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Course Offerings
                                        </Link>
                                        <Link
                                            href="/scholarships-and-grants"
                                            prefetch
                                            className={`${getDropdownLinkClass('/scholarships-and-grants')} text-sm`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Scholarships and Grants
                                        </Link>
                                    </div>
                                )}
                            </div>

                            <Link href="/careers" prefetch className={getLinkClass('/careers', false)} onClick={() => setMobileMenuOpen(false)}>
                                Careers
                            </Link>
                            <Link href="/contact" prefetch className={getLinkClass('/contact', false)} onClick={() => setMobileMenuOpen(false)}>
                                Contact
                            </Link>
                        </div>
                    </div>
                )}
            </nav>

            <style>{`
                .custom-scroll::-webkit-scrollbar {
                    width: 5px;
                }
                .custom-scroll::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scroll::-webkit-scrollbar-thumb {
                        background: rgba(0, 150, 63, 0.25);
                    border-radius: 10px;
                }
                .custom-scroll::-webkit-scrollbar-thumb:hover {
                        background: rgba(0, 150, 63, 0.45);
                }
            `}</style>
        </>
    );
}
